import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap, of } from 'rxjs';

import { FeatureflagsStateModel, FeatureflagsInitialState } from '@store/models/featureflags.model';
import {
  FetchFeatureflags,
  FetchFeatureflagsSuccess,
  FetchFeatureflagsFailure,
  UpdateFeatureflag,
  UpdateFeatureflagSuccess,
  UpdateFeatureflagFailure,
} from '@store/actions/featureflags.actions';
import { FeatureFlagsService, featureflagStorageKey } from '@services/feature-flags.service';
import { FeatureFlags } from '@models/featureflags.model';

@State<FeatureflagsStateModel>({
  name: 'featureFlags',
  defaults: FeatureflagsInitialState,
})
@Injectable()
export class FeatureflagsState {
  constructor(
    private readonly featureFlagsService: FeatureFlagsService,
  ) {}

  @Action(FetchFeatureflags)
  fetchFeatureflags(ctx: StateContext<FeatureflagsStateModel>) {
    ctx.patchState({ loading: true });

    return this.featureFlagsService.fetchFeatureflags().pipe(
      tap((response) => {
        ctx.dispatch(new FetchFeatureflagsSuccess(response.featureFlags));
      }),
      catchError(() => {
        ctx.dispatch(new FetchFeatureflagsFailure());
        return of(null);
      }),
    );
  }

  @Action(FetchFeatureflagsSuccess)
  fetchFeatureflagsSuccess(ctx: StateContext<FeatureflagsStateModel>, action: FetchFeatureflagsSuccess) {
    sessionStorage.setItem(featureflagStorageKey, JSON.stringify(action.featureFlags));
    ctx.patchState({
      featureFlags: action.featureFlags,
      loading: false,
      loaded: true,
    });
  }

  @Action(FetchFeatureflagsFailure)
  fetchFeatureflagsFailure(ctx: StateContext<FeatureflagsStateModel>) {
    // Still mark as loaded: guards waiting on readiness must resolve (and deny) rather than hang.
    ctx.patchState({
      featureFlags: {} as FeatureFlags,
      loading: false,
      loaded: true,
    });
  }

  @Action(UpdateFeatureflag)
  updateFeatureflag(ctx: StateContext<FeatureflagsStateModel>, action: UpdateFeatureflag) {
    ctx.patchState({ savingKey: action.key });

    return this.featureFlagsService.updateFeatureflags(action.key, action.value).pipe(
      tap((response) => {
        ctx.dispatch(new UpdateFeatureflagSuccess(response.featureFlags));
      }),
      catchError(() => {
        ctx.dispatch(new UpdateFeatureflagFailure());
        return of(null);
      }),
    );
  }

  @Action(UpdateFeatureflagSuccess)
  updateFeatureflagSuccess(ctx: StateContext<FeatureflagsStateModel>, action: UpdateFeatureflagSuccess) {
    // The API is leading: mirror its full response into both state and sessionStorage.
    sessionStorage.setItem(featureflagStorageKey, JSON.stringify(action.featureFlags));
    ctx.patchState({
      featureFlags: action.featureFlags,
      savingKey: null,
    });
  }

  @Action(UpdateFeatureflagFailure)
  updateFeatureflagFailure(ctx: StateContext<FeatureflagsStateModel>) {
    ctx.patchState({ savingKey: null });
  }
}
